import { useState, useEffect } from 'react';
import { IconSparkles, IconShieldCheck, IconLayoutRows, IconUser, IconDeviceFloppy, IconCamera } from '@tabler/icons-react';
import { showToast } from '../components/ui/Toast';
import { useSiteContent } from '../context/SiteContentContext';
import ImageUploader from '../components/ui/ImageUploader';
import { getPageImage, setPageImage, removePageImage } from '../lib/imageStore';

export default function AdminHomepage() {
  const { content, updateBulk } = useSiteContent();
  const [form, setForm] = useState({ ...content });
  const [saved, setSaved] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heroImg, setHeroImg] = useState(getPageImage('hero_main'));
  const [storyImg, setStoryImg] = useState(getPageImage('story_portrait'));

  useEffect(() => {
    if (Object.keys(content).length === 0) return;
    setForm(prev => (prev.hero_headline ? prev : { ...content }));
  }, [content]);

  function set(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  async function save() {
    if (saving) return;
    setSaving(true);
    const r = await updateBulk(form);
    setSaving(false);
    if (r && r.error) { showToast(r.error, 'error'); return; }
    setSaved(true);
    showToast('Homepage changes saved');
  }

  function field(key, label, type = 'input') {
    return (
      <div className="adm-field">
        <label className="adm-label">{label}</label>
        {type === 'textarea' ? (
          <textarea className="adm-textarea" value={form[key] || ''} onChange={e => set(key, e.target.value)} />
        ) : (
          <input className="adm-input" value={form[key] || ''} onChange={e => set(key, e.target.value)} />
        )}
      </div>
    );
  }

  return (
    <div>
      <h1 className="adm-title">Homepage text</h1>
      <p className="adm-sub">Every field here updates live on your website. Change text, save, and customers see it immediately.</p>

      <div className="adm-card">
        <div className="adm-card-title"><IconSparkles size={14} /> Hero section</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 16 }}>
          <div>
            {field('hero_eyebrow', 'Eyebrow line (small text above headline)')}
            {field('hero_headline', 'Main headline')}
            {field('hero_subtext', 'Subtext (2–3 sentences about your craft)', 'textarea')}
            <div className="adm-row2">
              {field('hero_cta_primary', 'Primary button label')}
              {field('hero_cta_secondary', 'Secondary button label')}
            </div>
          </div>
          <div>
            <label className="adm-label" style={{ marginBottom: 6, display: 'block' }}>
              <IconCamera size={12} style={{ verticalAlign: -1 }} /> Hero photo
            </label>
            <ImageUploader
              image={heroImg}
              onUpload={(data) => { setPageImage('hero_main', data); setHeroImg(data); }}
              onRemove={() => { removePageImage('hero_main'); setHeroImg(null); }}
              label="Hero photo"
              aspectRatio="3/4"
            />
          </div>
        </div>
      </div>

      <div className="adm-card">
        <div className="adm-card-title"><IconShieldCheck size={14} /> Trust bar (4 selling points)</div>
        <div className="adm-row2">
          {field('trust_1', 'Point 1')}
          {field('trust_2', 'Point 2')}
          {field('trust_3', 'Point 3')}
          {field('trust_4', 'Point 4')}
        </div>
      </div>

      <div className="adm-card">
        <div className="adm-card-title"><IconLayoutRows size={14} /> Collection section</div>
        {field('collection_eyebrow', 'Section eyebrow')}
        {field('collection_heading', 'Section heading')}
      </div>

      <div className="adm-card">
        <div className="adm-card-title"><IconUser size={14} /> Story section</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 16 }}>
          <div>
            {field('story_eyebrow', 'Eyebrow')}
            {field('story_heading', 'Heading')}
            {field('story_body', 'Body paragraph', 'textarea')}
            {field('story_link_text', 'Link text')}
          </div>
          <div>
            <label className="adm-label" style={{ marginBottom: 6, display: 'block' }}>
              <IconCamera size={12} style={{ verticalAlign: -1 }} /> Your portrait
            </label>
            <ImageUploader
              image={storyImg}
              onUpload={(data) => { setPageImage('story_portrait', data); setStoryImg(data); }}
              onRemove={() => { removePageImage('story_portrait'); setStoryImg(null); }}
              label="Portrait"
              aspectRatio="3/4"
            />
          </div>
        </div>
      </div>

      <div className="adm-card">
        <div className="adm-card-title">Newsletter</div>
        {field('newsletter_heading', 'Heading')}
        {field('newsletter_desc', 'Description')}
      </div>

      <div className="adm-card">
        <div className="adm-card-title">Footer</div>
        {field('footer_tagline', 'Tagline')}
        {field('footer_copyright', 'Copyright line')}
      </div>

      <div className="adm-save-bar">
        <span className={`adm-save-status ${saved ? 'adm-save-status--saved' : ''}`}>
          {saved ? '✓ All changes saved' : '● Unsaved changes'}
        </span>
        <button className="adm-btn" onClick={save}>
          <IconDeviceFloppy size={14} /> Save all changes
        </button>
      </div>
    </div>
  );
}